"use client";
import ColorNextSteps from "@/components/ColorNextSteps";

import { useState } from "react";
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  ThemeIcon,
  List,
  Alert,
  Divider,
  ColorSwatch,
  Paper,
  Grid,
  Accordion,
  Anchor,
} from "@mantine/core";
import {
  IconPalette,
  IconClock,
  IconCheck,
  IconArrowRight,
  IconPhoto,
  IconShirt,
  IconSparkles,
  IconUsers,
  IconStar,
  IconEye,
  IconHeart,
  IconUpload,
  IconShield,
  IconDownload,
  IconDroplet,
  IconRefresh,
  IconColorSwatch,
  IconFlower,
  IconSun,
  IconLeaf,
  IconSnowflake,
  IconChevronDown,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import { PRICING } from "@/constants/pricing";
import { colorSeasons } from "@/constants";
import UnifiedUpsell from "@/components/UnifiedUpsell";
import { useTranslations } from "next-intl";
import { trackCtaClick } from "@/lib/analytics";
import SixteenSeasons from "@/components/ShadeFinder/SixteenSeasons";

interface ColorSeasonsChartContentProps {
  locale: string;
}

export default function ColorSeasonsChartContent({ locale }: ColorSeasonsChartContentProps) {
  const t = useTranslations("colorSeasonsChart");
  // Transform the colorSeasons data into the format needed for the chart
  const seasons = Object.entries(colorSeasons).map(([seasonKey, seasonData]) => {
    const seasonIcons = {
      SPRING: IconFlower,
      SUMMER: IconSun,
      AUTUMN: IconLeaf,
      WINTER: IconSnowflake,
    };

    const seasonColors = {
      SPRING: "green",
      SUMMER: "blue",
      AUTUMN: "orange",
      WINTER: "violet",
    };

    return {
      name: seasonKey.charAt(0) + seasonKey.slice(1).toLowerCase(),
      icon: seasonIcons[seasonKey as keyof typeof seasonIcons],
      color: seasonColors[seasonKey as keyof typeof seasonColors],
      emoji: seasonData.emoji,
      subseasons: Object.entries(seasonData.subseasons).map(([subSeasonKey, subSeasonData]) => {
        // Show all 12 primary colors with names (editorial style)
        const displayColors = subSeasonData.primaryColors.slice(0, 12).map((color) => ({
          hex: color.hex,
          name: color.name,
        }));

        return {
          name: subSeasonKey
            .split("_")
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(" "),
          emoji: subSeasonData.emoji,
          colors: displayColors,
          description: subSeasonData.description,
          slug: subSeasonKey.toLowerCase().replace("_", "-"),
        };
      }),
    };
  });

  // Soft tinted gradients per season family (echoing the editorial palette pages)
  const seasonGradients: Record<string, string> = {
    Spring: "linear-gradient(135deg, #FBCEB115 0%, #FFE28A15 100%)",
    Summer: "linear-gradient(135deg, #C8D8E415 0%, #E8D8F015 100%)",
    Autumn: "linear-gradient(135deg, #D2691E15 0%, #B5651D15 100%)",
    Winter: "linear-gradient(135deg, #1F305E15 0%, #6F2DA815 100%)",
  };

  return (
    <Box>
      {/* ============================================ */}
      {/* HERO - Editorial style */}
      {/* ============================================ */}
      <Box
        style={{
          background:
            "linear-gradient(135deg, #FBCEB110 0%, #C8D8E410 33%, #D2691E10 66%, #1F305E10 100%)",
          paddingTop: 40,
          paddingBottom: 24,
        }}
      >
        <Container size="xl">
          <Box ta="center" maw={720} mx="auto">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={20}
              style={{ letterSpacing: "0.18em", fontWeight: 500 }}
            >
              Color Seasons Reference
            </Text>
            <Title
              order={1}
              style={{
                fontSize: "clamp(1.9rem, 6vw, 4rem)",
                fontWeight: 300,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
                marginBottom: 12,
              }}
            >
              {t("title")}
            </Title>
            <Text
              size="lg"
              c="dimmed"
              mb={0}
              style={{ fontWeight: 300, letterSpacing: "0.02em", lineHeight: 1.6 }}
            >
              {t("subtitle")}
            </Text>
            {locale === "en" && <Group justify="center" mt="md"><Anchor href="#12-season-palettes">Browse 12 palettes</Anchor><Anchor href="#16-season-color-analysis">Compare all 16 seasons</Anchor></Group>}
          </Box>
        </Container>
      </Box>

      {/* AT-A-GLANCE - all 12 seasons, above the fold */}
      <Container size="xl" pb={0} pt="md">
        <Box id="12-season-palettes" aria-label="All 12 color seasons">
          <SimpleGrid cols={{ base: 4, sm: 4 }} spacing={{ base: 6, sm: "md" }}>
            {seasons.map((season) => (
              <Stack key={season.name} gap={6}>
                <Text
                  ta="center"
                  fw={600}
                  c={season.color}
                  style={{ fontSize: "clamp(11px, 1.6vw, 15px)", letterSpacing: "0.04em" }}
                >
                  {season.name}
                </Text>
                {season.subseasons.map((subseason) => (
                  <Card
                    key={`${season.name}-${subseason.slug}`}
                    component={Link}
                    href={`/color-palette/${season.name.toLowerCase()}/${subseason.slug}`}
                    withBorder
                    p={{ base: 6, sm: "xs" }}
                    radius="md"
                    style={{ textDecoration: "none", background: "white" }}
                  >
                    <Text
                      size="xs"
                      fw={500}
                      c="dark"
                      lineClamp={1}
                      mb={4}
                      visibleFrom="sm"
                      style={{ fontSize: "clamp(9px, 1.4vw, 12px)" }}
                    >
                      {subseason.name} {season.name}
                    </Text>
                    <Text size="xs" fw={500} c="dark" lineClamp={1} mb={4} hiddenFrom="sm" style={{ fontSize: 10 }}>
                      {subseason.name}
                    </Text>
                    <Group gap={2} wrap="nowrap">
                      {subseason.colors.slice(0, 5).map((color, colorIndex) => (
                        <Box
                          key={colorIndex}
                          style={{
                            width: "100%",
                            aspectRatio: "1",
                            backgroundColor: color.hex,
                            borderRadius: 3,
                          }}
                        />
                      ))}
                    </Group>
                  </Card>
                ))}
              </Stack>
            ))}
          </SimpleGrid>
        </Box>
      </Container>

      <Container size="xl" pb="xl">
      <ColorNextSteps source="chart" />
      <Stack gap="xl">
        {/* THE CHART - Editorial-style season blocks */}
        <Box id="palettes">
          <Stack gap={64}>
            {seasons.map((season) => (
              <Box
                key={season.name}
                id={season.name.toLowerCase()}
                style={{
                  background: seasonGradients[season.name],
                  borderRadius: 16,
                  padding: "48px 32px",
                }}
              >
                <Box ta="center" mb={40}>
                  <Text
                    size="xs"
                    tt="uppercase"
                    c="dimmed"
                    mb={12}
                    style={{ letterSpacing: "0.18em", fontWeight: 500 }}
                  >
                    {season.emoji} {season.name} Family
                  </Text>
                  <Title
                    order={2}
                    style={{
                      fontSize: "clamp(2rem, 4vw, 2.75rem)",
                      fontWeight: 300,
                      letterSpacing: "-0.01em",
                      lineHeight: 1.15,
                    }}
                    c={season.color}
                  >
                    {season.name}
                  </Title>
                </Box>

                <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
                  {season.subseasons.map((subseason, index) => (
                    <Card
                      key={index}
                      withBorder
                      p="lg"
                      radius="md"
                      component={Link}
                      href={`/color-palette/${season.name.toLowerCase()}/${subseason.slug}`}
                      style={{
                        textDecoration: "none",
                        cursor: "pointer",
                        transition: "transform 0.15s, box-shadow 0.15s",
                        background: "white",
                      }}
                      className="season-card"
                    >
                      <Box mb="md">
                        <Text size="lg" mb={4}>
                          {subseason.emoji}
                        </Text>
                        <Title order={3} size="h4" fw={400} c="dark">
                          {subseason.name}
                        </Title>
                      </Box>

                      {/* Editorial-style 4x3 color grid: aspect-ratio tiles with names */}
                      <SimpleGrid cols={4} spacing={8} mb="md">
                        {subseason.colors.map((color, colorIndex) => (
                          <Box key={colorIndex} ta="center">
                            <Box
                              style={{
                                aspectRatio: "1",
                                backgroundColor: color.hex,
                                borderRadius: 6,
                                marginBottom: 4,
                              }}
                            />
                            <Text
                              size="xs"
                              c="dimmed"
                              style={{ fontSize: 9, lineHeight: 1.2 }}
                              lineClamp={1}
                            >
                              {color.name}
                            </Text>
                          </Box>
                        ))}
                      </SimpleGrid>

                      <Text size="xs" c="dimmed" lineClamp={2} mb="sm">
                        {subseason.description}
                      </Text>
                      <Group gap={4} c={season.color}>
                        <Text size="xs" fw={500}>
                          View full palette
                        </Text>
                        <IconArrowRight size={12} />
                      </Group>
                    </Card>
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* CTA after seeing the chart */}
        <Paper
          withBorder
          p="lg"
          radius="md"
          bg="violet"
          style={{ color: "white", borderColor: "transparent" }}
        >
          <Group justify="space-between" align="center" wrap="wrap" gap="md">
            <Box style={{ flex: "1 1 300px" }}>
              <Text size="lg" fw={600} mb={4}>
                {t("notSureWhichSeason")}
              </Text>
              <Text size="sm" style={{ opacity: 0.9 }}>
                {t("uploadForPalette")}
              </Text>
            </Box>
            <Button
              size="lg"
              component={Link}
              href="/ai-color-analysis?src=chart_hero"
              onClick={() => trackCtaClick("chart_hero")}
              rightSection={<IconArrowRight size={20} />}
              variant="white"
              color="violet"
              style={{ minWidth: 200 }}
            >
              {t("findMySeason")}
            </Button>
          </Group>
        </Paper>

        {/* 4 seasons vs 12 vs 16 */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            {t("fourVsTwelveVsSixteen")}
          </Title>
          <Text mb="lg">
            {t("systemsIntro")}
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                4 Seasons
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                The original system: Spring, Summer, Autumn, Winter. Great for beginners and general color guidance.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Spring: Warm + Clear + Light</List.Item>
                <List.Item>Summer: Cool + Muted + Light</List.Item>
                <List.Item>Autumn: Warm + Muted + Dark</List.Item>
                <List.Item>Winter: Cool + Clear + Dark</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="green">
                12 Seasons
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Adds subseasons for more precision. Each season has 3 variations based on dominant characteristics.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>True, Light, Bright (Spring)</List.Item>
                <List.Item>True, Light, Soft (Summer)</List.Item>
                <List.Item>True, Soft, Deep (Autumn)</List.Item>
                <List.Item>True, Bright, Deep (Winter)</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="orange">
                16 Seasons
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Some systems use four subtypes per season. Names and boundaries vary between analysts.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>More distinctions within each family</List.Item>
                <List.Item>Different naming systems exist</List.Item>
                <List.Item>More categories do not guarantee a better match</List.Item>
                <List.Item>Compare actual colors before choosing a label</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {locale === "en" && <SixteenSeasons />}

        {/* Quick ID traits */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            Quick ID Traits
          </Title>
          <Text mb="lg">Use these key characteristics to quickly identify which season family you belong to:</Text>
          <Grid>
            {seasons.map((season) => (
              <Grid.Col key={season.name} span={{ base: 12, md: 6 }}>
                <Card withBorder p="md" radius="md" h="100%">
                  <Group mb="md">
                    <ThemeIcon size={40} radius="xl" color={season.color} variant="light">
                      <season.icon size={20} />
                    </ThemeIcon>
                    <Title order={3} size="h5" c={season.color}>
                      {season.name}
                    </Title>
                  </Group>
                  <Text size="sm" c="dimmed" mb="md">
                    {season.name === "Spring" &&
                      "Warm undertones, light to medium coloring, clear and bright features. Best in warm, clear colors."}
                    {season.name === "Summer" &&
                      "Cool undertones, light to medium coloring, soft and muted features. Best in cool, muted colors."}
                    {season.name === "Autumn" &&
                      "Warm undertones, medium to dark coloring, rich and muted features. Best in warm, muted colors."}
                    {season.name === "Winter" &&
                      "Cool undertones, medium to dark coloring, clear and bright features. Best in cool, clear colors."}
                  </Text>
                  <Group gap="xs">
                    {season.subseasons[0].colors.slice(0, 5).map((color, index) => (
                      <ColorSwatch key={index} color={color.hex} size={20} />
                    ))}
                  </Group>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Card>

        {/* First AI-Verified Module */}
        <Card withBorder p="xl" radius="md" bg="gradient-to-br from-violet-50 to-purple-50">
          <Group align="center" wrap="wrap" gap="xl">
            <Box style={{ flex: "1 1 300px" }}>
              <Group mb="md">
                <ThemeIcon size="lg" radius="xl" variant="gradient" gradient={{ from: "violet", to: "grape" }}>
                  <IconSparkles size={24} />
                </ThemeIcon>
                <Title order={3} size="h4">
                  Not Sure Which Season You Are?
                </Title>
              </Group>
              <Text mb="md">
                Our digital color analysis uses your photos to determine your exact season and provides a personalized
                color palette tailored to your unique features.
              </Text>
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis?src=chart_inline1"
                onClick={() => trackCtaClick("chart_inline1")}
                rightSection={<IconArrowRight size={20} />}
                variant="gradient"
                gradient={{ from: "violet", to: "grape" }}
              >
                Try Digital Color Analysis
              </Button>
            </Box>
            <Box
              style={{
                flex: "1 1 300px",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <Image
                src="/outputs/deepWinter.png"
                alt="Deep Winter Color Analysis Example"
                width={400}
                height={400}
                style={{ objectFit: "contain", width: "100%", height: "auto" }}
              />
            </Box>
          </Group>
        </Card>


        {/* Nearby seasons */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            Nearby Seasons
          </Title>
          <Text mb="lg">
            If you're between seasons, you might be a transitional type. Here are the most common nearby season
            combinations:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                Cool Transitions
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Soft Summer ↔ Soft Autumn</List.Item>
                <List.Item>Cool Summer ↔ Cool Winter</List.Item>
                <List.Item>True Summer ↔ True Winter</List.Item>
                <List.Item>Light Summer ↔ Light Spring</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="orange">
                Warm Transitions
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Warm Autumn ↔ Warm Spring</List.Item>
                <List.Item>True Autumn ↔ True Spring</List.Item>
                <List.Item>Deep Autumn ↔ Deep Winter</List.Item>
                <List.Item>Bright Spring ↔ Bright Winter</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>


        {/* Detailed Season Characteristics */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Detailed Season Characteristics
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Understanding the specific traits of each season helps you identify your perfect match
          </Text>

          <Stack gap="xl">
            {seasons.map((season) => (
              <Box key={season.name}>
                <Group mb="md">
                  <ThemeIcon size={40} radius="xl" color={season.color} variant="light">
                    <season.icon size={20} />
                  </ThemeIcon>
                  <Title order={3} size="h4" c={season.color}>
                    {season.name} Season Characteristics
                  </Title>
                </Group>

                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
                  <Box>
                    <Title order={4} size="h5" mb="md">
                      Physical Traits
                    </Title>
                    <List size="sm" spacing="xs">
                      {season.name === "Spring" && (
                        <>
                          <List.Item>Warm undertones (golden, peachy)</List.Item>
                          <List.Item>Light to medium skin tone</List.Item>
                          <List.Item>Clear, bright eyes (blue, green, hazel)</List.Item>
                          <List.Item>Light to medium hair (blonde, light brown, red)</List.Item>
                          <List.Item>High contrast between features</List.Item>
                        </>
                      )}
                      {season.name === "Summer" && (
                        <>
                          <List.Item>Cool undertones (pink, blue, rosy)</List.Item>
                          <List.Item>Light to medium skin tone</List.Item>
                          <List.Item>Soft, muted eyes (blue, gray, green)</List.Item>
                          <List.Item>Light to medium hair (blonde, light brown, gray)</List.Item>
                          <List.Item>Low to medium contrast between features</List.Item>
                        </>
                      )}
                      {season.name === "Autumn" && (
                        <>
                          <List.Item>Warm undertones (golden, peachy, olive)</List.Item>
                          <List.Item>Medium to dark skin tone</List.Item>
                          <List.Item>Rich, warm eyes (brown, hazel, green)</List.Item>
                          <List.Item>Medium to dark hair (brown, auburn, red)</List.Item>
                          <List.Item>Medium to high contrast between features</List.Item>
                        </>
                      )}
                      {season.name === "Winter" && (
                        <>
                          <List.Item>Cool undertones (pink, blue, rosy)</List.Item>
                          <List.Item>Medium to dark skin tone</List.Item>
                          <List.Item>Clear, bright eyes (blue, brown, hazel)</List.Item>
                          <List.Item>Dark hair (brown, black, dark auburn)</List.Item>
                          <List.Item>High contrast between features</List.Item>
                        </>
                      )}
                    </List>
                  </Box>

                  <Box>
                    <Title order={4} size="h5" mb="md">
                      Color Preferences
                    </Title>
                    <List size="sm" spacing="xs">
                      {season.name === "Spring" && (
                        <>
                          <List.Item>Warm, clear, bright colors</List.Item>
                          <List.Item>Best in: coral, peach, golden yellow</List.Item>
                          <List.Item>Warm greens and blues</List.Item>
                          <List.Item>Clear, bright pastels</List.Item>
                          <List.Item>Avoid: muted, dark, cool colors</List.Item>
                        </>
                      )}
                      {season.name === "Summer" && (
                        <>
                          <List.Item>Cool, muted, soft colors</List.Item>
                          <List.Item>Best in: rose, lavender, powder blue</List.Item>
                          <List.Item>Soft, muted pastels</List.Item>
                          <List.Item>Cool grays and taupes</List.Item>
                          <List.Item>Avoid: bright, warm, clear colors</List.Item>
                        </>
                      )}
                      {season.name === "Autumn" && (
                        <>
                          <List.Item>Warm, muted, rich colors</List.Item>
                          <List.Item>Best in: rust, olive, golden brown</List.Item>
                          <List.Item>Rich, earthy tones</List.Item>
                          <List.Item>Warm, muted colors</List.Item>
                          <List.Item>Avoid: bright, cool, clear colors</List.Item>
                        </>
                      )}
                      {season.name === "Winter" && (
                        <>
                          <List.Item>Cool, clear, bright colors</List.Item>
                          <List.Item>Best in: royal blue, fuchsia, pure white</List.Item>
                          <List.Item>Clear, bright colors</List.Item>
                          <List.Item>High contrast combinations</List.Item>
                          <List.Item>Avoid: muted, warm, soft colors</List.Item>
                        </>
                      )}
                    </List>
                  </Box>
                </SimpleGrid>
              </Box>
            ))}
          </Stack>
        </Card>

        {/* How to Use This Chart */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            How to Use This Color Seasons Chart
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Follow these steps to identify your perfect color season using this comprehensive chart
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} size="h4" mb="md" c="violet">
                Step 1: Identify Your Undertone
              </Title>
              <Text mb="md">
                Start by determining whether you have warm, cool, or neutral undertones. This is the foundation of your
                color season.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Look at the veins on your wrist</List.Item>
                <List.Item>Try on gold vs silver jewelry</List.Item>
                <List.Item>Hold white paper next to your face</List.Item>
                <List.Item>Consider how you tan or burn</List.Item>
              </List>
              {locale === "en" && (
                <Text size="sm" mt="md">
                  Getting mixed answers? Our <Anchor component={Link} href="/undertone-quiz">warm or cool undertone quiz</Anchor>
                  {" "}helps you check your starting point before choosing a season.
                </Text>
              )}
            </Box>

            <Box>
              <Title order={3} size="h4" mb="md" c="blue">
                Step 2: Assess Your Contrast Level
              </Title>
              <Text mb="md">
                Determine whether you have high, medium, or low contrast between your skin, hair, and eyes.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>High contrast: Dark hair, light skin</List.Item>
                <List.Item>Medium contrast: Similar tones</List.Item>
                <List.Item>Low contrast: Very similar tones</List.Item>
                <List.Item>Consider your natural coloring</List.Item>
              </List>
            </Box>

            <Box>
              <Title order={3} size="h4" mb="md" c="green">
                Step 3: Determine Your Chroma
              </Title>
              <Text mb="md">
                Chroma refers to the intensity or saturation of your coloring - whether it's clear/bright or muted/soft.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Clear/Bright: Vivid, intense coloring</List.Item>
                <List.Item>Muted/Soft: Soft, subdued coloring</List.Item>
                <List.Item>Look at your natural hair and eye color</List.Item>
                <List.Item>Consider how colors affect your appearance</List.Item>
              </List>
            </Box>

            <Box>
              <Title order={3} size="h4" mb="md" c="orange">
                Step 4: Find Your Season
              </Title>
              <Text mb="md">Combine your undertone, contrast level, and chroma to identify your color season.</Text>
              <List size="sm" spacing="xs">
                <List.Item>Warm + Clear + Light = Spring</List.Item>
                <List.Item>Cool + Muted + Light = Summer</List.Item>
                <List.Item>Warm + Muted + Dark = Autumn</List.Item>
                <List.Item>Cool + Clear + Dark = Winter</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Second Module */}
        <Card withBorder p="xl" radius="md" bg="gradient-to-br from-violet-50 to-purple-50">
          <Group align="center" wrap="wrap" gap="xl">
            <Box
              style={{
                flex: "1 1 300px",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <Image
                src="/outputs/softAutumn.png"
                alt="Soft Autumn Color Analysis Example"
                width={400}
                height={400}
                style={{ objectFit: "contain", width: "100%", height: "auto" }}
              />
            </Box>
            <Box style={{ flex: "1 1 300px" }}>
              <Group mb="md">
                <ThemeIcon size="lg" radius="xl" variant="gradient" gradient={{ from: "violet", to: "grape" }}>
                  <IconSparkles size={24} />
                </ThemeIcon>
                <Title order={3} size="h4">
                  Still Unsure About Your Season?
                </Title>
              </Group>
              <Text mb="md">
                Get a personalized analysis based on your photos. You'll receive your verified season, a complete color
                palette, and styling recommendations.
              </Text>
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis?src=chart_inline2"
                onClick={() => trackCtaClick("chart_inline2")}
                rightSection={<IconArrowRight size={20} />}
                variant="gradient"
                gradient={{ from: "violet", to: "grape" }}
              >
                Try Digital Color Analysis
              </Button>
            </Box>
          </Group>
        </Card>

        {/* Common Misconceptions */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Common Misconceptions About Color Seasons
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Debunking the most common myths about seasonal color analysis
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} size="h4" mb="md" c="red">
                ❌ Common Myths
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>"I can't wear black because I'm a Spring"</List.Item>
                <List.Item>"All redheads are Autumns"</List.Item>
                <List.Item>"You can only wear colors from your season"</List.Item>
                <List.Item>"Your season changes with age"</List.Item>
                <List.Item>"You can't be between seasons"</List.Item>
                <List.Item>"Only women can do color analysis"</List.Item>
              </List>
            </Box>

            <Box>
              <Title order={3} size="h4" mb="md" c="green">
                ✅ The Truth
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Some colors work better, but you can wear any color</List.Item>
                <List.Item>Hair color is just one factor in determining season</List.Item>
                <List.Item>You can wear colors from adjacent seasons</List.Item>
                <List.Item>Your undertone stays the same throughout life</List.Item>
                <List.Item>Many people are transitional between seasons</List.Item>
                <List.Item>Color analysis works for all genders and ages</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Professional vs DIY Analysis */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Professional vs DIY Color Analysis
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Understanding the differences between professional analysis and DIY methods
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} size="h4" mb="md" c="blue">
                Professional Analysis
              </Title>
              <Text mb="md">
                Professional color analysts use specialized tools and extensive training to determine your season.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Uses professional color drapes</List.Item>
                <List.Item>Tests in controlled lighting</List.Item>
                <List.Item>Considers multiple factors simultaneously</List.Item>
                <List.Item>Provides detailed explanations</List.Item>
                <List.Item>Can identify subtle variations</List.Item>
                <List.Item>Costs $150-400 per session</List.Item>
                <List.Item>Most accurate results</List.Item>
              </List>
            </Box>

            <Box>
              <Title order={3} size="h4" mb="md" c="green">
                DIY Methods
              </Title>
              <Text mb="md">DIY methods are accessible and can be quite accurate when done correctly.</Text>
              <List size="sm" spacing="xs">
                <List.Item>Free and convenient</List.Item>
                <List.Item>Can be done at home</List.Item>
                <List.Item>Multiple tests for verification</List.Item>
                <List.Item>Good starting point for color analysis</List.Item>
                <List.Item>May need multiple attempts</List.Item>
                <List.Item>Results may be less precise</List.Item>
                <List.Item>Great for learning about color theory</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* FAQ Block */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Frequently Asked Questions
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            Common questions about color season analysis
          </Text>

          <Accordion variant="separated" radius="md">
            <Accordion.Item value="between-seasons">
              <Accordion.Control icon={<IconSparkles size={20} />}>
                Can I be between seasons?
              </Accordion.Control>
              <Accordion.Panel>
                <Text mb="md">
                  Yes! Many people fall between two adjacent seasons, especially if they have neutral undertones or mixed
                  characteristics. This is completely normal and is often called being a "transitional" or "blended" type.
                </Text>
                <Text mb="md">
                  If you find yourself between seasons, you can wear colors from both palettes, focusing on the overlapping
                  shades that work well for both. Common transitional combinations include Soft Summer/Soft Autumn, Light
                  Summer/Light Spring, and Deep Autumn/Deep Winter.
                </Text>
                <Text size="sm">
                  <Anchor component={Link} href="/color-analysis-quiz?src=faq1" fw={600} c="violet">
                    Start Free Quiz →
                  </Anchor>
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="phone-cameras">
              <Accordion.Control icon={<IconPhoto size={20} />}>
                Do phone cameras affect results?
              </Accordion.Control>
              <Accordion.Panel>
                <Text mb="md">
                  Phone cameras can affect color accuracy, but our AI is trained to account for common camera variations and
                  lighting conditions. For best results, take photos in natural daylight near a window, without makeup, and
                  avoid heavy filters or editing.
                </Text>
                <Text mb="md">
                  Our AI analyzes multiple factors including your skin's undertone, hair color, eye color, and overall
                  contrast level. By looking at these elements together, we can provide accurate results even with typical
                  smartphone photos.
                </Text>
                <Text size="sm">
                  <Anchor component={Link} href="/color-analysis-quiz?src=faq2" fw={600} c="violet">
                    Start Free Quiz →
                  </Anchor>
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="ai-better">
              <Accordion.Control icon={<IconSparkles size={20} />}>
                How is AI better than charts?
              </Accordion.Control>
              <Accordion.Panel>
                <Text mb="md">
                  AI analysis offers several advantages over traditional charts: it analyzes your unique combination of
                  features simultaneously, accounts for subtle variations that charts might miss, and provides personalized
                  results rather than forcing you into rigid categories.
                </Text>
                <Text mb="md">
                  While charts are great for learning color theory and understanding the different seasons, AI can process
                  hundreds of data points from your photos to determine your season with professional-level accuracy. Plus,
                  you get instant results for just ${PRICING.AI_COLOR_ANALYSIS.BASE_PRICE} instead of paying $150-400 for an
                  in-person consultation.
                </Text>
                <Text size="sm">
                  <Anchor component={Link} href="/color-analysis-quiz?src=faq3" fw={600} c="violet">
                    Start Free Quiz →
                  </Anchor>
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="season-change">
              <Accordion.Control icon={<IconRefresh size={20} />}>
                Does my season change with age or hair color?
              </Accordion.Control>
              <Accordion.Panel>
                <Text mb="md">
                  Your underlying season (determined by your natural undertone) doesn't change, but your coloring can shift
                  with age. Hair may gray, skin may lose some pigmentation, and these changes can move you within your season
                  family (e.g., from Bright Spring to Light Spring) or to an adjacent season.
                </Text>
                <Text mb="md">
                  If you dye your hair, your season analysis should be based on your natural coloring for the most accurate
                  results. However, you can adjust your color choices to complement your current hair color while still
                  working within your seasonal palette.
                </Text>
                <Text size="sm">
                  <Anchor component={Link} href="/color-analysis-quiz?src=faq4" fw={600} c="violet">
                    Start Free Quiz →
                  </Anchor>
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Card>

        <Text ta="center" size="sm" c="dimmed">
          New to all this? Read the{" "}
          <Anchor component={Link} href="/how-to-do-color-analysis">
            full at-home color analysis guide
          </Anchor>
          .
        </Text>

        <UnifiedUpsell
          title="Ready to Find Your Perfect Season?"
          subtitle="Take our comprehensive color analysis quiz or try our AI analysis to discover your exact color season and get your personalized palette. Start your journey to looking and feeling your best."
        />
      </Stack>
      </Container>

      {/* Floating CTA for mobile */}
      <Box
        hiddenFrom="md"
        style={{
          position: "fixed",
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1000,
        }}
      >
        <Button
          size="lg"
          fullWidth
          component={Link}
          href="/ai-color-analysis?src=chart_floating"
          onClick={() => trackCtaClick("chart_floating")}
          rightSection={<IconArrowRight size={18} />}
          color="violet"
        >
          Try Digital Color Analysis
        </Button>
      </Box>

      {/* Spacer for floating button */}
      <Box hiddenFrom="md" h={80} />
    </Box>
  );
}
